import { useTranslation } from "react-i18next";
import RedirectingComponent from "src/chamber/components/RedirectingComponent";
import { Box } from "@mui/system";
import { useGetOrganizationsForAdmin } from "src/complaint/data-access/useGetOrganizationsForAdmin";

type filters = {
  chamberUsers: { label: string; value: number; isChamberAdmin: boolean }[];
  violationTypes: { label: string; value: number }[];
  isOrgAdmin: boolean;
  isSampleInitiator: boolean;
  violationStatuses: {
    descr: string;
    id: number;
  }[];
};

type badgeCountsViolationCases = {
  activeOwnViolationCasesCount: number | null;
  unassignedViolationCasesCount: number | null;
  countsLoading?: boolean;
};
type ViolationCasesRedirectorProps = filters & badgeCountsViolationCases;

function ViolationCasesRedirector(props: ViolationCasesRedirectorProps) {
  const { t } = useTranslation();
  const { data: organizationsData } = useGetOrganizationsForAdmin(
    props.isSampleInitiator,
  );
  const allFilters = [
    {
      filterName: "violationTypeId",
      filterLabel: t("Είδος παράβασης"),
      filterOptions: props?.violationTypes?.map((option) => ({
        label: option?.label,
        value: option?.value,
      })),
    },
    {
      filterName: "chamberAdmin",
      filterLabel: t("Υπεύθυνος προϊστάμενος"),
      filterOptions: props.chamberUsers
        .filter((user) => user.isChamberAdmin)
        .map((user) => ({
          label: user.label,
          value: user.value,
        })),
    },
    {
      filterName: "chamberUser",
      filterLabel: t("Υπεύθυνος διεκπεραίωσης"),
      filterOptions: props.chamberUsers
        .filter((user) => !user.isChamberAdmin)
        .map((user) => ({
          label: user.label,
          value: user.value,
        })),
    },
    {
      filterName: "violationCaseStatusId",
      filterLabel: t("Κατάσταση παράβασης - προστίμου"),
      filterOptions: props.violationStatuses.map((violationStatus) => ({
        label: violationStatus.descr,
        value: violationStatus.id,
      })),
    },
  ];
  const redirectors = [
    ...(props.isSampleInitiator
      ? [
          {
            linkText: t(
              "Υποθέσεις παραβάσεων - προστίμων (όλων των Υ.Γ.Ε.ΜΗ.)",
            ),
            linkTo: "/violation/admin/casesDashboard",
            badgeCount: null,
            filters: [
              ...allFilters.filter((filter) =>
                ["violationTypeId", "violationCaseStatusId"].includes(
                  filter.filterName,
                ),
              ),
              {
                filterName: "organizationId",
                filterLabel: t("Υ.Γ.Ε.ΜΗ."),
                filterOptions:
                  organizationsData?.data?.map((org) => ({
                    value: org.id,
                    label: org.label,
                  })) ?? [],
              },
            ],
          },
        ]
      : []),
    {
      linkText: props.isOrgAdmin
        ? t("Υποθέσεις σε εξέλιξη (δικές μου ή γενικά στην Υ.Γ.Ε.ΜΗ.)")
        : t("Υποθέσεις σε εξέλιξη (δικές μου)"),
      linkTo: props.isOrgAdmin
        ? "/violation/chamber/casesDashboard"
        : "/violation/chamber/myChamberUserCasesDashboard",
      badgeCount: props.activeOwnViolationCasesCount,
      badgeLoading: props.countsLoading,
      filters: allFilters.filter((filter) =>
        ["violationTypeId", "violationCaseStatusId"].includes(
          filter?.filterName,
        ),
      ),
    },
    ...(props.isOrgAdmin
      ? [
          {
            linkText: t("Μη ανατεθειμένες υποθέσεις παραβάσεων"),
            linkTo: "/violation/chamber/assignChamberUserDashboard",
            badgeCount: props.unassignedViolationCasesCount,
            badgeLoading: props.countsLoading,
            filters: allFilters.filter((filter) =>
              ["violationTypeId", "violationCaseStatusId"].includes(
                filter?.filterName,
              ),
            ),
          },
        ]
      : []),
    {
      linkText: props.isOrgAdmin
        ? t("Ολοκληρωμένες υποθέσεις παράβασης - προστίμου")
        : t("Υποθέσεις ολοκληρωμένες (δικές μου)"),
      linkTo: props.isOrgAdmin
        ? t("/violation/chamber/pastViolationCases")
        : "/violation/chamber/myChamberUserCompletedCasesDashboard",
      badgeCount: null,
      filters: allFilters.filter((filter) =>
        ["violationTypeId", "violationCaseStatusId"].includes(
          filter?.filterName,
        ),
      ),
    },
  ];

  return (
    <Box
      sx={{
        padding: "1rem",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {redirectors.map((redirector) => (
        <RedirectingComponent {...redirector} />
      ))}
    </Box>
  );
}

export default ViolationCasesRedirector;
